import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, CheckCircle2, XCircle } from "lucide-react";
import AsaLogo from "../components/AsaLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("This reset link is invalid or incomplete.");
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      // Strip the token from the URL/history for safety
      window.history.replaceState({}, document.title, "/asa/reset-password");
      setDone(true);
      toast.success("Password reset successful");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "This reset link is invalid or has expired.");
    }
    setLoading(false);
  };

  const field = "w-full h-12 pl-11 pr-4 rounded-lg border border-slate-200 text-[15px] outline-none focus:border-[#0d9488] transition-colors";

  return (
    <div className="min-h-screen bg-[#f4f8f7] flex flex-col items-center justify-center px-4">
      <div className="mb-8"><AsaLogo size={38} /></div>
      <div className="w-full max-w-[440px] bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 size={44} className="text-[#22a35a] mx-auto" />
            <h1 className="font-head font-bold text-slate-800 text-xl mt-4">Password updated</h1>
            <p className="text-slate-500 text-sm mt-2">You can now sign in with your new password.</p>
            <button onClick={() => navigate("/asa/login")} data-testid="reset-goto-login" className="w-full h-12 mt-6 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold transition-colors">Go to log in</button>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-invalid">
            <XCircle size={44} className="text-[#e0567a] mx-auto" />
            <h1 className="font-head font-bold text-slate-800 text-xl mt-4">Invalid reset link</h1>
            <p className="text-slate-500 text-sm mt-2">This link is missing or incomplete. Please request a new password reset.</p>
            <button onClick={() => navigate("/asa/forgot-password")} className="w-full h-12 mt-6 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold transition-colors">Request new link</button>
          </div>
        ) : (
          <>
            <h1 className="font-head font-bold text-slate-800 text-2xl text-center">Choose a new password</h1>
            <p className="text-center text-slate-500 text-sm mt-1 mb-6">Enter and confirm your new password below.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={19} />
                <input type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 8 characters)" className={field} data-testid="reset-password-input" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={19} />
                <input type="password" required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className={field} data-testid="reset-confirm-input" />
              </div>
              <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="w-full h-12 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-60 text-white font-semibold transition-colors">{loading ? "Updating…" : "Reset password"}</button>
            </form>
          </>
        )}
      </div>
      <p className="text-[12px] text-slate-400 mt-6">© {new Date().getFullYear()} ASA Foundation · Copyright &amp; IP: Asal Shirazi</p>
    </div>
  );
}
